import React, { useMemo } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Box, Typography } from '@mui/material';
import { useBusinessUuid } from './useBusinessUuid';
import { colors } from './constants';

import Dashboard from './screens/Dashboard';
import Products from './screens/Products';
import ProductDetail from './screens/ProductDetail';
import InventoryLots from './screens/InventoryLots';
import Vendors from './screens/Vendors';
import PurchaseInvoices from './screens/PurchaseInvoices';
import PurchaseInvoiceCreate from './screens/PurchaseInvoiceCreate';
import PurchaseInvoiceDetail from './screens/PurchaseInvoiceDetail';
import InvoiceImportReview from './screens/InvoiceImportReview';
import Imports from './screens/Imports';
import ImportDetail from './screens/ImportDetail';
import BulkImport from './screens/BulkImport';
import Warehouses from './screens/Warehouses';
import WarehouseDetail from './screens/WarehouseDetail';
import StockTransfers from './screens/StockTransfers';
import StockTransferCreate from './screens/StockTransferCreate';
import StockTransferDetail from './screens/StockTransferDetail';
import StockAdjustments from './screens/StockAdjustments';
import StockReservations from './screens/StockReservations';
import StockReservationCreate from './screens/StockReservationCreate';
import StockReservationDetail from './screens/StockReservationDetail';
import InventoryCounts from './screens/InventoryCounts';
import InventoryCountCreate from './screens/InventoryCountCreate';
import InventoryCountDetail from './screens/InventoryCountDetail';
import Reports from './screens/Reports';
import Alerts from './screens/Alerts';
import Settings from './screens/Settings';
import UnitsOfMeasure from './screens/UnitsOfMeasure';

/**
 * Entry point for the "Inventory Management" section. Owns a React Query client
 * scoped to this section (server state stays out of global Redux) and the
 * section's nested routes. The inventory backend is business-scoped, so a
 * business must be selected first.
 */
export default function InventoryManagement() {
  const businessUuid = useBusinessUuid();
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 15000 },
        },
      }),
    []
  );

  if (!businessUuid) {
    return (
      <Box sx={{ py: 10, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ color: colors.gray600 }}>
          Select a business to manage inventory
        </Typography>
        <Typography variant="body2" sx={{ color: colors.gray500, mt: 1 }}>
          Inventory data is scoped to the active business.
        </Typography>
      </Box>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <Switch>
          <Route
            exact
            path="/inventory-management"
            render={() => <Redirect to="/inventory-management/dashboard" />}
          />
          <Route exact path="/inventory-management/dashboard" component={Dashboard} />

          <Route exact path="/inventory-management/products" component={Products} />
          <Route
            exact
            path="/inventory-management/products/:uuid"
            component={ProductDetail}
          />

          <Route
            exact
            path="/inventory-management/inventory-lots"
            component={InventoryLots}
          />

          <Route exact path="/inventory-management/vendors" component={Vendors} />

          <Route
            exact
            path="/inventory-management/purchase-invoices"
            component={PurchaseInvoices}
          />
          <Route
            exact
            path="/inventory-management/purchase-invoices/new"
            component={PurchaseInvoiceCreate}
          />
          <Route
            exact
            path="/inventory-management/purchase-invoices/imports/:uuid"
            component={InvoiceImportReview}
          />
          <Route
            exact
            path="/inventory-management/purchase-invoices/:uuid"
            component={PurchaseInvoiceDetail}
          />

          <Route exact path="/inventory-management/import" component={Imports} />
          <Route
            exact
            path="/inventory-management/import/new"
            component={BulkImport}
          />
          <Route
            exact
            path="/inventory-management/import/:uuid"
            component={ImportDetail}
          />

          <Route exact path="/inventory-management/warehouses" component={Warehouses} />
          <Route
            exact
            path="/inventory-management/warehouses/:uuid"
            component={WarehouseDetail}
          />

          <Route
            exact
            path="/inventory-management/stock-transfers"
            component={StockTransfers}
          />
          <Route
            exact
            path="/inventory-management/stock-transfers/new"
            component={StockTransferCreate}
          />
          <Route
            exact
            path="/inventory-management/stock-transfers/:uuid"
            component={StockTransferDetail}
          />

          <Route
            exact
            path="/inventory-management/stock-adjustments"
            component={StockAdjustments}
          />

          <Route
            exact
            path="/inventory-management/stock-reservations"
            component={StockReservations}
          />
          <Route
            exact
            path="/inventory-management/stock-reservations/new"
            component={StockReservationCreate}
          />
          <Route
            exact
            path="/inventory-management/stock-reservations/:uuid"
            component={StockReservationDetail}
          />

          <Route
            exact
            path="/inventory-management/inventory-counts"
            component={InventoryCounts}
          />
          <Route
            exact
            path="/inventory-management/inventory-counts/new"
            component={InventoryCountCreate}
          />
          <Route
            exact
            path="/inventory-management/inventory-counts/:uuid"
            component={InventoryCountDetail}
          />

          <Route path="/inventory-management/reports" component={Reports} />

          <Route exact path="/inventory-management/alerts" component={Alerts} />

          <Route
            exact
            path="/inventory-management/settings/units-of-measure"
            component={UnitsOfMeasure}
          />
          <Route exact path="/inventory-management/settings" component={Settings} />

          <Redirect to="/inventory-management/dashboard" />
        </Switch>
      </Box>
    </QueryClientProvider>
  );
}
